import { useSyncExternalStore } from 'react';

import { type OutreachStep } from '@apps/web/lib/outreach_utils';

/**
 * What starting an outreach sets in motion, across the whole playbook.
 *
 * Every dispatchable step fires on its own schedule once the outreach starts,
 * so naming only the first understates what was authorized. A step the agency
 * cannot dispatch is excluded rather than named.
 */
export type OutreachSequence =
    | 'text_once'
    | 'text_repeat'
    | 'call_only'
    | 'text_then_call'
    | 'call_then_text'
    | 'nothing';

export function getOutreachSequence(
    steps: ReadonlyArray<Pick<OutreachStep, 'step_type'>>,
    { canPlaceCalls }: { readonly canPlaceCalls: boolean },
): OutreachSequence {
    // A voice step dispatches nothing unless the organization has a
    // voice-enabled number in this mode — the worker drops it silently
    // (`outreach_dispatch_voice.py`). Dropping those steps here keeps the
    // sentence to what will actually go out, so an unprovisioned agency is
    // never promised a call that cannot be placed.
    const dispatchable = canPlaceCalls
        ? steps
        : steps.filter((step) => step.step_type !== 'voice');

    const textSteps = dispatchable.filter(
        (step) => step.step_type === 'sms',
    ).length;
    const hasCall = dispatchable.some((step) => step.step_type === 'voice');

    if (textSteps === 0 && !hasCall) {
        // Nothing will dispatch — an empty playbook, or a voice-only one on an
        // agency that cannot call. Say so: this is the one case where the
        // outreach starts and no caregiver ever hears from it, and a coordinator
        // who reads "Contacting 8 caregivers" here would wait for replies that
        // are never coming.
        return 'nothing';
    }
    if (!hasCall) {
        return textSteps > 1 ? 'text_repeat' : 'text_once';
    }
    if (textSteps === 0) {
        return 'call_only';
    }
    return dispatchable[0]?.step_type === 'voice'
        ? 'call_then_text'
        : 'text_then_call';
}

/**
 * The sentence the selection bar shows while an outreach dispatches.
 *
 * Reported at the moment of action rather than behind a confirmation step:
 * starting an outreach is the most-travelled path in the product, so a modal
 * would tax every send to warn about the rare one.
 */
export function getOutreachStartedReport({
    caregiverCount,
    sequence,
}: {
    readonly caregiverCount: number;
    readonly sequence: OutreachSequence;
}): string {
    const people = `${caregiverCount} caregiver${caregiverCount === 1 ? '' : 's'}`;
    switch (sequence) {
        case 'text_once':
            return `Texting ${people}`;
        case 'text_repeat':
            return `Texting ${people}, and again if no one replies`;
        case 'call_only':
            return `Calling ${people}`;
        case 'text_then_call':
            return `Texting ${people}, then calling anyone who hasn’t replied`;
        case 'call_then_text':
            return `Calling ${people}, then texting anyone who doesn’t answer`;
        case 'nothing':
            return `Started for ${people}, but no steps will run`;
        default: {
            const exhaustive: never = sequence;
            return exhaustive;
        }
    }
}

// Well past these endpoints' normal latency, so a slow start still completes;
// short enough that a connection that never settles doesn't strand the caller.
const START_OUTREACH_TIMEOUT_MS = 20_000;

// Deliberately not "we couldn't start it": we stopped waiting, but the server
// may have gone on to start the outreach, and the UI must not claim otherwise.
export const STILL_STARTING_REPORT_TEXT =
    'Still starting. Reload to see if it went out.';

/**
 * Bound a start request so a connection that never settles can't strand the UI.
 *
 * The api client leaves POSTs unbounded on purpose — aborting one can't stop the
 * server finishing it, so a slow synchronous POST must not become a false
 * failure. That reasoning holds here, but an unbounded request left the caller
 * spinning forever with no way out. So bound it and word the outcome honestly.
 * Retrying is safe: contact attempts are unique per caregiver, round and
 * delivery mode (`uq_contact_attempts_caregiver_target`), so a second start
 * texts nobody twice. Passing `idempotent` instead would also switch on two
 * automatic retries (three attempts total) and could count the outreach start
 * up to three times in PostHog.
 *
 * Shared by every path that starts an outreach — the callout page's two submit
 * handlers, the chat recommendation carousel, and the add-caregivers modal — so
 * none of them can be left unbounded.
 */
export function createStartOutreachTimeout(): {
    readonly signal: AbortSignal;
    readonly clear: () => void;
} {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
        () => controller.abort(),
        START_OUTREACH_TIMEOUT_MS,
    );
    return {
        signal: controller.signal,
        clear: () => window.clearTimeout(timeoutId),
    };
}

export type StartReportStatus = 'sending' | 'started' | 'failed';

export type StartReport = {
    readonly outreachId: string;
    readonly status: StartReportStatus;
    readonly text: string;
};

// The spinner holds for MINIMUM_SENDING_MS whatever happens, and keeps spinning
// past it while the request is still in flight — so it always reads as work in
// progress, never as a flicker. The tick then confirms briefly. A local POST
// returns in a couple hundred milliseconds, which is why the floor exists at
// all: without it there is nothing to see. The bar's own push and reveal
// animations all run well under a second and are independent of this floor.
const MINIMUM_SENDING_MS = 2000;
const STARTED_VISIBLE_MS = 2000;
// Failure holds longer: it has to be read and acted on, not just acknowledged.
const FAILED_VISIBLE_MS = 8000;

// Kept outside React so the report survives anything that unmounts the detail
// view mid-send — a revalidation, a status transition, a route re-render. State
// on the component made the report's lifetime depend on the page's, which is
// exactly the coupling that made it vanish before it could be read.
let current: StartReport | null = null;
let sentAt: number | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function emit(next: StartReport | null): void {
    current = next;
    for (const listener of listeners) {
        listener();
    }
}

function clearTimer(): void {
    if (timer) {
        clearTimeout(timer);
        timer = null;
    }
}

export function subscribeToStartReport(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function readStartReport(outreachId: string): StartReport | null {
    return current?.outreachId === outreachId ? current : null;
}

/**
 * Subscribe to the report for one outreach.
 *
 * Keyed by id because the store holds a single report: without the guard a
 * report published on one outreach would surface on whichever outreach the
 * coordinator navigated to next, for as long as it stayed visible.
 */
export function useStartReport(outreachId: string): StartReport | null {
    return useSyncExternalStore(
        subscribeToStartReport,
        () => readStartReport(outreachId),
        () => null,
    );
}

/**
 * Clear the report without showing an outcome.
 *
 * For failures that surface their own way — a toast, the conflict banner — the
 * bar must still stop claiming a send is in flight.
 */
export function dismissStartReport(): void {
    clearTimer();
    sentAt = null;
    emit(null);
}

export function publishStartReport(next: StartReport): void {
    clearTimer();

    if (next.status === 'sending') {
        sentAt = Date.now();
        emit(next);
        return;
    }

    const shownFor = sentAt === null ? MINIMUM_SENDING_MS : Date.now() - sentAt;
    sentAt = null;

    if (next.status === 'failed') {
        // No floor on a failure: the spinner is claiming work is in progress
        // that has already stopped, and the sooner that is corrected the
        // sooner the coordinator can retry.
        emit(next);
        timer = setTimeout(() => emit(null), FAILED_VISIBLE_MS);
        return;
    }

    // Keep spinning until the floor is met. A request slower than the floor
    // has already outlasted it, so the tick lands as soon as it returns.
    timer = setTimeout(
        () => {
            emit(next);
            timer = setTimeout(() => emit(null), STARTED_VISIBLE_MS);
        },
        Math.max(MINIMUM_SENDING_MS - shownFor, 0),
    );
}

/**
 * Drop any in-flight report and its timers.
 *
 * Module-level state outlives a test file's renders, so a report published by
 * one case would otherwise surface in the next. Deliberately leaves `listeners`
 * alone: `useSyncExternalStore` never re-subscribes on its own, so clearing
 * them would permanently detach every mounted subscriber. Tests unmount their
 * own trees, which unsubscribes properly.
 */
export function resetStartReport(): void {
    clearTimer();
    sentAt = null;
    current = null;
}
